"use client";

import { useEffect, useMemo, useState } from "react";

import Image from "next/image";

import { useFrontPage } from "@/hooks/self-schedule/use-front-page";

import { cx } from "@/lib/tailwind-utils";

import {
  SelfScheduleStep,
  useSelfScheduleStore,
} from "@/stores/self-schedule-store";

interface ClinicImage {
  imageSrc: string;
  title: string;
}

export function ClinicCarousel({
  className,
  overrideImageUrl,
  forceExpanded = false,
  overrideImageAlt,
  bannerAspect = false,
}: {
  className?: string;
  overrideImageUrl?: string | null;
  forceExpanded?: boolean;
  overrideImageAlt?: string;
  /** Render the override image as a 4:1 tenant banner instead of the fixed header height. */
  bannerAspect?: boolean;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentStep = useSelfScheduleStore((s) => s.currentStep);

  const { data: frontPageData } = useFrontPage();
  const clinicImages = useMemo(
    () =>
      frontPageData?.locations
        .filter((location) => Boolean(location.photoUrl))
        .map((location) => ({
          imageSrc: location.photoUrl,
          title: location.name,
        })) as ClinicImage[] | undefined,
    [frontPageData?.locations],
  );

  // Auto-advance carousel every 3 seconds
  useEffect(() => {
    if (overrideImageUrl || !clinicImages || clinicImages.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % clinicImages.length);
    }, 10000);

    return () => clearInterval(interval);
  }, [clinicImages, overrideImageUrl]);

  const collapsed =
    !forceExpanded &&
    ((!overrideImageUrl && (!clinicImages || clinicImages.length === 0)) ||
      currentStep === SelfScheduleStep.BOOKING_COMPLETE);

  return (
    <div
      className={cx(
        "relative w-full max-w-full overflow-hidden rounded-b-xl transition-all duration-300",
        collapsed && "!h-10 !shadow-none md:!h-20",
        className,
        // Uploaded banners keep the 4:1 shape the settings page recommends.
        bannerAspect &&
          overrideImageUrl &&
          !collapsed &&
          "aspect-[4/1] !h-auto md:!h-auto",
      )}
    >
      {overrideImageUrl ? (
        <Image
          src={overrideImageUrl}
          alt={overrideImageAlt ?? "Self-scheduling banner"}
          fill
          priority
          sizes="(min-width: 768px) 896px, 100vw"
          className="object-cover"
        />
      ) : (
        clinicImages?.map((image, index) => (
          <Image
            src={image.imageSrc}
            alt={image.title}
            key={image.imageSrc}
            width={1000}
            height={200}
            priority={index === 0}
            aria-hidden={index !== currentIndex}
            className={cx(
              "absolute inset-0 h-full w-full object-cover transition-opacity duration-700",
              index === currentIndex ? "opacity-100" : "opacity-0",
            )}
          />
        ))
      )}
    </div>
  );
}
